<template>
  <div class="tutorial-container p-3">
    <!-- Header Principal -->
    <div class="box box-primary tutorial-header-box">
      <div class="box-body tutorial-header-content">
        <div class="row align-items-center">
          <div class="col-md-8">
            <h2 class="tutorial-title">
              <i class="fa fa-graduation-cap text-primary"></i> Centro de Tutoriales y Guía de Uso
            </h2>
            <p class="text-muted tutorial-subtitle">
              Aprenda a parametrizar y operar cada uno de los módulos de la plataforma SGP con explicaciones detalladas, ejemplos prácticos y simuladores interactivos.
            </p>
          </div>
          <div class="col-md-4">
            <div class="input-group search-box">
              <span class="input-group-addon"><i class="fa fa-search"></i></span>
              <input 
                type="text" 
                class="form-control" 
                v-model="searchQuery" 
                placeholder="Buscar tema, ej: compensado, prenoc, fraccionado, nocturno..."
              />
              <span class="input-group-btn" v-if="searchQuery">
                <button class="btn btn-default" @click="searchQuery = ''"><i class="fa fa-times"></i></button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navegación por Categorías -->
    <div class="row">
      <div class="col-md-3">
        <div class="box box-solid">
          <div class="box-header with-border">
            <h3 class="box-title"><i class="fa fa-list-ul"></i> Módulos y Secciones</h3>
          </div>
          <div class="box-body no-padding">
            <ul class="nav nav-pills nav-stacked tutorial-sidebar">
              <li :class="{ active: activeSection === 'shifts' }">
                <a href="#" @click.prevent="activeSection = 'shifts'">
                  <i class="fa fa-clock-o text-blue"></i> 1. Gestión de Turnos
                  <span class="label label-primary pull-right">8 Guías</span>
                </a>
              </li>
              <li :class="{ active: activeSection === 'assignment' }">
                <a href="#" @click.prevent="activeSection = 'assignment'">
                  <i class="fa fa-calendar-check-o text-green"></i> 2. Malla y Asignación
                  <span class="label label-default pull-right">Próximamente</span>
                </a>
              </li>
              <li :class="{ active: activeSection === 'settlement' }">
                <a href="#" @click.prevent="activeSection = 'settlement'">
                  <i class="fa fa-calculator text-yellow"></i> 3. Liquidador de Horas
                  <span class="label label-default pull-right">Próximamente</span>
                </a>
              </li>
              <li :class="{ active: activeSection === 'permissions' }">
                <a href="#" @click.prevent="activeSection = 'permissions'">
                  <i class="fa fa-exclamation-triangle text-purple"></i> 4. Permisos y Novedades
                  <span class="label label-default pull-right">Próximamente</span>
                </a>
              </li>
              <li :class="{ active: activeSection === 'agreements' }">
                <a href="#" @click.prevent="activeSection = 'agreements'">
                  <i class="fa fa-balance-scale text-red"></i> 5. Acuerdos Laborales
                  <span class="label label-default pull-right">Próximamente</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Widget de Ayuda / Info -->
        <div class="box box-info">
          <div class="box-body">
            <h5><i class="fa fa-lightbulb-o text-yellow"></i> ¿Dudas frecuentes?</h5>
            <small class="text-muted">
              Si necesita soporte sobre una regla de nómina específica de su empresa, puede consultar la sección de <b>Acuerdos Laborales</b> o contactar al administrador del sistema.
            </small>
          </div>
        </div>
      </div>

      <!-- Área de Contenido Principal -->
      <div class="col-md-9">

        <!-- ========================================== -->
        <!-- SECCIÓN 1: GESTIÓN DE TURNOS               -->
        <!-- ========================================== -->
        <div v-if="activeSection === 'shifts'">

          <!-- Card Informativa Principal -->
          <div class="box box-primary">
            <div class="box-header with-border">
              <h3 class="box-title">
                <i class="fa fa-clock-o text-blue"></i> Módulo 1: Guía Completa de Configuración de Turnos
              </h3>
              <div class="box-tools pull-right">
                <button class="btn btn-xs btn-primary" @click="expandAll = !expandAll">
                  <i :class="expandAll ? 'fa fa-compress' : 'fa fa-expand'"></i> 
                  {{ expandAll ? 'Colapsar todo' : 'Expandir todo' }}
                </button>
              </div>
            </div>
            <div class="box-body">
              <p class="lead">
                En SGP, un <b>Turno</b> define el horario teórico de trabajo de un empleado, las tolerancias de entrada/salida, los tiempos de descanso y las reglas especiales para el cálculo de recargos nocturnos, dominicales y festivos.
              </p>

              <!-- Simulador / Selector de Turno Rápido -->
              <div class="well simulator-well">
                <h4 class="text-primary"><i class="fa fa-magic"></i> Simulador Rápido de Tipos de Turno</h4>
                <p class="text-muted small">Seleccione un tipo de turno para previsualizar su concepto y resumen de comportamiento en el liquidador:</p>

                <div class="btn-group btn-group-justified margin-bottom" role="group">
                  <div class="btn-group" role="group">
                    <button type="button" class="btn" :class="selectedSimShift === 'DEFAULT' ? 'btn-primary' : 'btn-default'" @click="selectedSimShift = 'DEFAULT'">
                      <i class="fa fa-sun-o"></i> Ordinario (`DEFAULT`)
                    </button>
                  </div>
                  <div class="btn-group" role="group">
                    <button type="button" class="btn" :class="selectedSimShift === 'PRENOC' ? 'btn-primary' : 'btn-default'" @click="selectedSimShift = 'PRENOC'">
                      <i class="fa fa-moon-o"></i> Día Anterior (`PRENOC`)
                    </button>
                  </div>
                  <div class="btn-group" role="group">
                    <button type="button" class="btn" :class="selectedSimShift === 'FRAC' ? 'btn-primary' : 'btn-default'" @click="selectedSimShift = 'FRAC'">
                      <i class="fa fa-cutlery"></i> Fraccionado (`FRAC`)
                    </button>
                  </div>
                  <div class="btn-group" role="group">
                    <button type="button" class="btn" :class="selectedSimShift === 'FRAC_NOC' ? 'btn-primary' : 'btn-default'" @click="selectedSimShift = 'FRAC_NOC'">
                      <i class="fa fa-moon-o"></i> Frac. Nocturno (`FRAC_NOC`)
                    </button>
                  </div>
                  <div class="btn-group" role="group">
                    <button type="button" class="btn" :class="selectedSimShift === 'CMP' ? 'btn-primary' : 'btn-default'" @click="selectedSimShift = 'CMP'">
                      <i class="fa fa-calendar-check-o"></i> Compensado (`CMP`)
                    </button>
                  </div>
                  <div class="btn-group" role="group">
                    <button type="button" class="btn" :class="selectedSimShift === 'NIGHT' ? 'btn-primary' : 'btn-default'" @click="selectedSimShift = 'NIGHT'">
                      <i class="fa fa-adjust"></i> Nocturno / Mixto
                    </button>
                  </div>
                </div>

                <!-- Detalles del Simulador Seleccionado -->
                <div class="sim-output box-body-light p-3">
                  <div v-if="selectedSimShift === 'DEFAULT'">
                    <span class="label label-primary">Tipo: DEFAULT</span>
                    <span class="label label-info">Jornada Continua</span>
                    <h4 class="margin-top-5">Turno Ordinario Estándar (Ej: 08:00 - 17:00)</h4>
                    <p>
                      Jornada continua de 8 horas de trabajo efectivo con 1 hora de descanso intermedio. Adecuado para personal administrativo u operativo con horarios fijos diurnos.
                    </p>
                    <div class="timeline-preview">
                      <span class="time-badge bg-blue">08:00 Entrada</span>
                      <span class="time-badge bg-gray">12:00 - 13:00 Almuerzo</span>
                      <span class="time-badge bg-green">17:00 Salida</span>
                    </div>
                  </div>

                  <div v-if="selectedSimShift === 'PRENOC'">
                    <span class="label label-danger">Tipo: PRENOC</span>
                    <span class="label label-inverse bg-navy">Inicia Día Calendario Previo</span>
                    <h4 class="margin-top-5">Nocturno empezando el día anterior (`PRENOC`)</h4>
                    <p>
                      Diseñado para jornadas nocturnas que inician físicamente en las últimas horas del día calendario previo (ej: Domingo a las 22:00) pero pertenecen administrativa y legalmente a la jornada laboral del día siguiente (ej: Lunes).
                    </p>
                    <div class="timeline-preview">
                      <span class="time-badge bg-red">Domingo 22:00 (Entrada previa)</span>
                      <span class="time-badge bg-black">00:00 Medianoche</span>
                      <span class="time-badge bg-green">Lunes 06:00 (Salida - Jornada del Lunes)</span>
                    </div>
                  </div>

                  <div v-if="selectedSimShift === 'FRAC'">
                    <span class="label label-warning">Tipo: FRAC</span>
                    <span class="label label-info">Jornada Dividida</span>
                    <h4 class="margin-top-5">Turno Fraccionado (Ej: 08:00-12:00 y 14:00-18:00)</h4>
                    <p>
                      El empleado realiza 2 entradas y 2 salidas en el mismo día. La plataforma exige validación de marcaciones en ambos bloques de tiempo.
                    </p>
                    <div class="timeline-preview">
                      <span class="time-badge bg-blue">08:00 Ent 1</span>
                      <span class="time-badge bg-orange">12:00 Sal 1</span>
                      <span class="time-badge bg-purple">14:00 Ent 2</span>
                      <span class="time-badge bg-green">18:00 Sal 2</span>
                    </div>
                  </div>

                  <div v-if="selectedSimShift === 'FRAC_NOC'">
                    <span class="label label-danger">Tipo: FRAC_NOC</span>
                    <span class="label label-inverse bg-navy">Cruza Medianoche (+1 día)</span>
                    <h4 class="margin-top-5">Turno Fraccionado Nocturno (Ej: 22:00 a 08:00 +1 día)</h4>
                    <p>
                      Jornada dividida que inicia en las horas nocturnas del día programado (ej: 22:00) y finaliza en la mañana del día siguiente (ej: 08:00). Permite 2 tramos de trabajo en el rango nocturno con horas por turno definidas manualmente (ej: 8h).
                    </p>
                    <div class="timeline-preview">
                      <span class="time-badge bg-navy">22:00 Ent 1 (Día A)</span>
                      <span class="time-badge bg-black">01:00 Sal 1 (Día B)</span>
                      <span class="time-badge bg-purple">03:00 Ent 2 (Día B)</span>
                      <span class="time-badge bg-green">08:00 Sal 2 (Día B)</span>
                    </div>
                  </div>

                  <div v-if="selectedSimShift === 'CMP'">
                    <span class="label label-success">Tipo: CMP / Descanso</span>
                    <span class="label label-danger">Especial Dominical</span>
                    <h4 class="margin-top-5">Turno Compensatorio de Descanso</h4>
                    <p>
                      Se asigna un día en semana (ej: Miércoles) para dar descanso remunerado al trabajador por haber laborado un domingo/festivo. Cambia el recargo dominical del 190% (concepto `011`) al 90% (concepto `1011`).
                    </p>
                    <div class="timeline-preview">
                      <span class="time-badge bg-red">Domingo: Laborado (8h)</span>
                      <span class="time-badge bg-teal">Miércoles: Turno `CMP` (Descanso)</span>
                      <span class="time-badge bg-green">Liquidador: Dominical Compensado (`1011`)</span>
                    </div>
                  </div>

                  <div v-if="selectedSimShift === 'NIGHT'">
                    <span class="label label-inverse bg-navy">Cruce de Medianoche</span>
                    <span class="label label-primary">Recargo Nocturno</span>
                    <h4 class="margin-top-5">Turno Nocturno / Mixto (Ej: 22:00 - 06:00)</h4>
                    <p>
                      Turno que inicia en una fecha y finaliza a la mañana siguiente. SGP realiza la división automática de horas ordinarias y recargos nocturnos/festivos nocturnos.
                    </p>
                    <div class="timeline-preview">
                      <span class="time-badge bg-navy">22:00 Entrada (Día A)</span>
                      <span class="time-badge bg-black">00:00 Medianoche (Cruce)</span>
                      <span class="time-badge bg-blue">06:00 Salida (Día B)</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- GUÍAS DETALLADAS EN ACCORDION -->
          <div class="panel-group" id="accordionShifts">

            <!-- 1. TURNO COMPENSADO (DETALLADO) -->
            <div class="panel panel-default" v-if="matchesSearch('compensado dominical festivo descanso cmp 1011 011')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapseCmp" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-calendar-check-o"></i> <b>1. Turno Compensado (Domingos y Festivos Compensados)</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapseCmp" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('comp') }]">
                <div class="panel-body">
                  <div class="callout callout-info">
                    <h4><i class="fa fa-info-circle"></i> Concepto Clave</h4>
                    <p>
                      El <b>Turno Compensado</b> se utiliza cuando un empleado labora un domingo o festivo y se le concede un día de descanso remunerado en la misma semana laboral (o cuando la rotación fija de su turno ya remunera la jornada festiva).
                    </p>
                  </div>

                  <h4><i class="fa fa-cogs"></i> Las 2 Modalidades de Configuración en SGP</h4>
                  
                  <div class="row margin-top">
                    <div class="col-md-6">
                      <div class="box box-primary box-solid">
                        <div class="box-header with-border">
                          <h3 class="box-title">Modalidad A: Turno `CMP` en Semana</h3>
                        </div>
                        <div class="box-body">
                          <ul>
                            <li><b>Configuración:</b> Se crea un turno con código/tipo <code>CMP</code> de 0 horas (descanso).</li>
                            <li><b>Asignación:</b> Se programa al empleado el domingo con su turno de trabajo y en semana (ej: Miércoles) con el turno <code>CMP</code>.</li>
                            <li><b>Cálculo:</b> El sistema detecta el <code>CMP</code> en la semana y cambia automáticamente el recargo del domingo de <b>No Compensado (190% / Concepto 011)</b> a <b>Compensado (90% / Concepto 1011)</b>.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="box box-success box-solid">
                        <div class="box-header with-border">
                          <h3 class="box-title">Modalidad B: Checkbox en el Turno</h3>
                        </div>
                        <div class="box-body">
                          <ul>
                            <li><b>Configuración:</b> En <i>Configuración → Turnos</i>, al editar un turno ordinario, se marca la casilla <b>"Domingos y festivos compensados"</b>.</li>
                            <li><b>Asignación:</b> Se asigna normalmente.</li>
                            <li><b>Cálculo:</b> Todo domingo/festivo trabajado en este turno se liquida como <b>Compensado</b> sin necesidad de asignar un casillero <code>CMP</code> explícito en la semana.</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h4><i class="fa fa-list-ol"></i> Ejemplo Práctico Paso a Paso</h4>
                  <ol class="steps-list">
                    <li>
                      <b>Crear Turno CMP:</b> Vaya a <i>Configuración → Turnos</i>. Cree un turno llamado <code>COMPENSATORIO</code> con tipo <b>Compensatorio / Descanso</b> y horario de <code>00:00</code> a <code>00:00</code>.
                    </li>
                    <li>
                      <b>Asignar Malla:</b> En <i>Asignación de Turnos</i>, programe al empleado:
                      <ul>
                        <li><b>Domingo:</b> Turno 06:00 - 14:00 (Trabaja 8h).</li>
                        <li><b>Miércoles:</b> Turno <code>CMP</code> (Descansa).</li>
                      </ul>
                    </li>
                    <li>
                      <b>Verificar Liquidador:</b> Al procesar las marcaciones en el liquidador, el domingo registrará 8 horas en la columna <b>Recargo Dominical Compensado (Concepto 1011)</b>.
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            <!-- 2. TURNO NOCTURNO INICIANDO EL DIA ANTERIOR (PRENOC) -->
            <div class="panel panel-default" v-if="matchesSearch('nocturno empezando dia anterior prenoc previo calendario domingo lunes 22:00')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapsePrenoc" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-moon-o"></i> <b>2. Turno "Nocturno Iniciando el Día Anterior" (`PRENOC`)</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapsePrenoc" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('prenoc') || searchQuery.toLowerCase().includes('anterior') }]">
                <div class="panel-body">
                  <div class="callout callout-info">
                    <h4><i class="fa fa-info-circle"></i> ¿Qué es el Turno PRENOC?</h4>
                    <p>
                      En muchas operaciones de producción continuas o turnos rotativos nocturnos, la jornada laboral asignada a un día específico (por ejemplo: el <b>Lunes</b>) comienza físicamente a las últimas horas del día calendario anterior (por ejemplo: <b>Domingo a las 22:00</b>).
                    </p>
                    <p>
                      El tipo de turno <b>"Nocturno empezando el día anterior" (<code>PRENOC</code>)</b> le indica a SGP que aunque la marcación de entrada del empleado ocurra el día calendario previo (domingo 22:00), esa marcación pertenece formalmente y se vincula al día de programación asignado (lunes).
                    </p>
                  </div>

                  <h4><i class="fa fa-cogs"></i> ¿Por qué es necesario utilizar el tipo PRENOC?</h4>
                  <ul>
                    <li><b>Evita falsas ausencias / llegadas tarde:</b> Si programa un turno normal de 22:00 a 06:00 el lunes, el sistema esperaría la entrada el lunes a las 22:00. Si el trabajador ingresa el domingo a las 22:00, un turno regular generaría una entrada no programada el domingo y una ausencia injustificada el lunes. Con <code>PRENOC</code> se corrige este comportamiento.</li>
                    <li><b>Asociación Correcta de Marcaciones:</b> Con <code>PRENOC</code>, la entrada capturada la noche previa se imputa al expediente laboral del día asignado.</li>
                    <li><b>Cálculo Exacto de Recargos:</b> El motor liquida las horas del tramo previo (ej: 22:00 a 00:00 del domingo) como recargo festivo/dominical nocturno y las horas posteriores a medianoche (00:00 a 06:00 del lunes) como recargo nocturno ordinario.</li>
                  </ul>

                  <h4><i class="fa fa-list-ol"></i> Ejemplo Práctico de Configuración paso a paso</h4>
                  <ol class="steps-list">
                    <li>
                      <b>Creación en Configuración → Turnos:</b>
                      <ul>
                        <li><b>Nombre del Turno:</b> <code>NOCTURNO DÍA ANTERIOR (22:00 - 06:00)</code></li>
                        <li><b>Tipo de Turno:</b> Seleccionar <b>"Nocturno empezando el día anterior" (<code>PRENOC</code>)</b>.</li>
                        <li><b>Hora Inicio / Fin:</b> <code>22:00</code> a <code>06:00</code>.</li>
                        <li><b>Horas Laborables:</b> <code>8.00</code> horas.</li>
                      </ul>
                    </li>
                    <li>
                      <b>Asignación en Malla:</b>
                      <p>Programe el turno <code>PRENOC</code> en el día <b>Lunes</b> para los empleados de la cuadrilla nocturna.</p>
                    </li>
                    <li>
                      <b>Resultado de Marcación y Liquidador:</b>
                      <ul>
                        <li><b>Domingo 21:50 (Biométrico):</b> El empleado marca su entrada.</li>
                        <li><b>Lunes 06:05 (Biométrico):</b> El empleado marca su salida.</li>
                        <li><b>Resumen SGP:</b> SGP registra la asistencia completa dentro de la tarjeta del <b>Lunes</b>, contabilizando 2.00 Horas Recargo Dominical Nocturno (22:00-00:00) y 6.00 Horas Recargo Nocturno Ordinario (00:00-06:00).</li>
                      </ul>
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            <!-- 3. TURNO ORDINARIO / FIJO -->
            <div class="panel panel-default" v-if="matchesSearch('ordinario fijo default horas extra diurna no diurno 08:00')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapseDefault" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-sun-o"></i> <b>3. Turno Ordinario / Fijo (`DEFAULT`)</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapseDefault" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('ord') }]">
                <div class="panel-body">
                  <p>
                    El turno <b>DEFAULT</b> representa la jornada continua tradicional (por ejemplo, de 07:00 a 16:00 o de 08:00 a 17:00) donde el empleado registra una marcación de entrada al iniciar su día y una de salida al finalizar.
                  </p>

                  <h4><i class="fa fa-sliders"></i> Parámetros de Configuración Recomendados</h4>
                  <table class="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Campo</th>
                        <th>Descripción / Recomendación</th>
                        <th>Ejemplo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><b>Nombre del Turno</b></td>
                        <td>Nombre descriptivo que identifique el rango horario.</td>
                        <td><code>TURNO DIURNO 8H (08-17)</code></td>
                      </tr>
                      <tr>
                        <td><b>Tipo de Turno</b></td>
                        <td>Seleccionar <b>DEFAULT</b>.</td>
                        <td><code>DEFAULT</code></td>
                      </tr>
                      <tr>
                        <td><b>Hora Inicio / Fin</b></td>
                        <td>Horario teórico oficial del turno.</td>
                        <td><code>08:00</code> a <code>17:00</code></td>
                      </tr>
                      <tr>
                        <td><b>Horas Laborables</b></td>
                        <td>Horas efectivas de trabajo (excluyendo almuerzo no pagado).</td>
                        <td><code>8.00</code> Horas</td>
                      </tr>
                      <tr>
                        <td><b>Tiempo de Descanso</b></td>
                        <td>Tiempo asignado para la comida/almuerzo (en minutos).</td>
                        <td><code>60</code> Minutos</td>
                      </tr>
                      <tr>
                        <td><b>Tolerancia Entrada/Salida</b></td>
                        <td>Minutos de gracia antes de marcar llegada tarde o salida anticipada.</td>
                        <td><code>10</code> Minutos</td>
                      </tr>
                    </tbody>
                  </table>

                  <h4><i class="fa fa-check-circle"></i> Ejemplo de Liquidación:</h4>
                  <p>
                    Si un empleado con turno 08:00-17:00 ingresa a las 07:55 y sale a las 18:30:
                  </p>
                  <ul>
                    <li><b>Horas Ordinarias:</b> 8.00 horas.</li>
                    <li><b>Horas Extra Diurnas:</b> 1.50 horas (de 17:00 a 18:30, si el acuerdo laboral lo autoriza).</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- 4. TURNO FRACCIONADO -->
            <div class="panel panel-default" v-if="matchesSearch('fraccionado frac dividida dos bloques marcaciones almuerzo')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapseFrac" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-cutlery"></i> <b>4. Turno Fraccionado (`FRAC`)</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapseFrac" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('frac') }]">
                <div class="panel-body">
                  <div class="callout callout-info">
                    <h4><i class="fa fa-info-circle"></i> Procesamiento de Marcaciones en Turnos Fraccionados</h4>
                    <p>
                      El sistema agrupa automáticamente las marcaciones del día en <b>parejas ordenadas cronológicamente (Entrada y Salida)</b>.
                      Aunque el caso típico de turno partido comprende <b>4 marcaciones al día</b> (Entrada 1, Salida 1, Entrada 2, Salida 2), el motor admite desde <b>2 marcaciones</b> (1 bloque) hasta <b>múltiples marcaciones (2, 4, 6 o más)</b>.
                    </p>
                  </div>

                  <h4><i class="fa fa-clock-o"></i> Configuración de Intervalos</h4>
                  <p>Al crear un turno con tipo <b>FRAC</b> en <i>Configuración → Turnos</i>, aparecerán los campos para registrar ambos bloques:</p>
                  
                  <div class="row">
                    <div class="col-md-6">
                      <div class="well">
                        <h5><b>Bloque 1 (Mañana / Primer Tramo)</b></h5>
                        <p><b>Hora Inicio 1:</b> 08:00</p>
                        <p><b>Hora Fin 1:</b> 12:00</p>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="well">
                        <h5><b>Bloque 2 (Tarde / Segundo Tramo)</b></h5>
                        <p><b>Hora Inicio 2:</b> 14:00</p>
                        <p><b>Hora Fin 2:</b> 18:00</p>
                      </div>
                    </div>
                  </div>

                  <p><b>Total horas trabajadas:</b> 4h (Bloque 1) + 4h (Bloque 2) = <b>8.00 Horas Laborables</b>.</p>
                </div>
              </div>
            </div>

            <!-- 4.1 TURNO FRACCIONADO NOCTURNO -->
            <div class="panel panel-default" v-if="matchesSearch('fraccionado nocturno frac_noc medianoche cruce 22:00 08:00')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapseFracNoc" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-moon-o"></i> <b>4.1 Turno Fraccionado Nocturno (`FRAC_NOC`)</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapseFracNoc" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('frac_noc') || searchQuery.toLowerCase().includes('nocturno') }]">
                <div class="panel-body">
                  <div class="callout callout-info">
                    <h4><i class="fa fa-info-circle"></i> Concepto del Turno Fraccionado Nocturno</h4>
                    <p>
                      El turno <b>FRAC_NOC</b> se utiliza cuando un empleado realiza una jornada dividida que inicia en las horas nocturnas del día programado (ejemplo: <b>22:00</b>) y concluye en la mañana del día siguiente (ejemplo: <b>08:00</b>).
                    </p>
                  </div>
                  <h4><i class="fa fa-cogs"></i> Parámetros de Configuración</h4>
                  <ul>
                    <li><b>Tipo de Turno:</b> Seleccionar <code>FRAC_NOC</code> (Turno Fraccionado Nocturno).</li>
                    <li><b>Entrada / Salida Ventana:</b> <code>22:00</code> a <code>08:00</code> (El sistema reconoce que la salida ocurre al día siguiente).</li>
                    <li><b>Horas por turno (Manual):</b> Definir manualmente el total de horas a laborar (ej: <code>8.00</code> hs).</li>
                    <li><b>Marcación continua (Tolerancia):</b> Configurar la ventana en minutos (ej: 30 min) para diferenciar pequeñas salidas de descansos/fracciones.</li>
                  </ul>
                  <h4><i class="fa fa-check-circle"></i> Ejemplo de Proceso y Marcaciones</h4>
                  <p>Si la ventana es de 22:00 a 08:00 con 8.0 horas objetivo:</p>
                  <ul>
                    <li><b>22:00 (Entrada 1 - Día 1):</b> Inicia primer tramo.</li>
                    <li><b>01:00 (Salida 1 - Día 2):</b> Concluye primer tramo (3 horas).</li>
                    <li><b>03:00 (Entrada 2 - Día 2):</b> Inicia segundo tramo.</li>
                    <li><b>08:00 (Salida 2 - Día 2):</b> Concluye jornada (5 horas).</li>
                    <li><b>Resultado Liquidador:</b> 8 horas ordinarias totales asignadas a la jornada del Día 1, aplicando automáticamente los recargos nocturnos correspondientes (21:00/22:00 a 06:00).</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- 5. TURNO NOCTURNO Y MIXTO -->
            <div class="panel panel-default" v-if="matchesSearch('nocturno mixto noche cruce medianoche recargo 21:00 22:00 06:00')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapseNight" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-adjust"></i> <b>5. Turnos Nocturnos y Mixtos (Cruce de Medianoche)</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapseNight" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('noct') }]">
                <div class="panel-body">
                  <p>
                    Un turno es **Nocturno o Mixto** cuando la hora de finalización es menor a la hora de inicio (ejemplo: <code>22:00</code> a <code>06:00</code> del día siguiente).
                  </p>

                  <div class="box box-solid box-default">
                    <div class="box-body">
                      <h4><i class="fa fa-adjust"></i> División Automática de Recargos en SGP</h4>
                      <p>
                        SGP evalúa el horario legal de recargo nocturno según el **Acuerdo Laboral** (ejemplo: de 21:00 a 06:00 o de 22:00 a 06:00) y clasifica automáticamente cada tramo de la jornada:
                      </p>
                      <ul>
                        <li><b>Tramo Diurno (si aplica):</b> Horas laboradas antes del inicio del horario nocturno.</li>
                        <li><b>Recargo Nocturno (RN):</b> Horas ordinarias dentro de la franja nocturna (se aplica recargo, ej: +35%).</li>
                        <li><b>Cruce de Día / Festivo:</b> Si la medianoche cruza hacia un día festivo o domingo, el tramo posterior a la 00:00 se liquida automáticamente como <b>Recargo Festivo Nocturno</b>.</li>
                      </ul>
                    </div>
                  </div>

                  <h4><i class="fa fa-list-ol"></i> Ejemplo de Configuración:</h4>
                  <ul>
                    <li><b>Nombre:</b> <code>TURNO NOCTURNO (22:00 - 06:00)</code></li>
                    <li><b>Hora Inicio:</b> <code>22:00</code></li>
                    <li><b>Hora Fin:</b> <code>06:00</code></li>
                    <li><b>Horas Laborables:</b> <code>8.00</code></li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- 6. TURNOS DE GUARDIA / DISPONIBILIDAD -->
            <div class="panel panel-default" v-if="matchesSearch('guardia disponibilidad reten standby guard')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapseGuard" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-shield"></i> <b>6. Turnos de Guardia / Disponibilidad (`GUARD`)</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapseGuard" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('guar') }]">
                <div class="panel-body">
                  <p>
                    Los turnos de tipo <b>GUARD</b> o <b>Disponibilidad</b> se aplican a personal técnico o médico que debe estar localizable fuera de su jornada ordinaria para atender emergencias.
                  </p>

                  <ul>
                    <li><b>Comportamiento:</b> No genera descuento por falta de marcación si no hubo llamado de emergencia.</li>
                    <li><b>Atención de Llamados:</b> Si el empleado realiza marcación de entrada/salida durante su disponibilidad, SGP liquida esas horas como **Horas Extra por Llamado de Guardia** según la convención configurada.</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- 7. TOLERANCIAS Y REGLAS ADICIONALES -->
            <div class="panel panel-default" v-if="matchesSearch('tolerancias gracia tardanza anticipada redondeo marco')">
              <div class="panel-heading bg-light-blue-gradient text-white">
                <h4 class="panel-title">
                  <a data-toggle="collapse" data-parent="#accordionShifts" href="#collapseRules" :aria-expanded="expandAll ? 'true' : 'false'">
                    <i class="fa fa-check-square-o"></i> <b>7. Tolerancias de Marcación y Redondeos</b>
                    <span class="pull-right"><i class="fa fa-chevron-down"></i></span>
                  </a>
                </h4>
              </div>
              <div id="collapseRules" :class="['panel-collapse collapse', { in: expandAll || searchQuery.toLowerCase().includes('tole') }]">
                <div class="panel-body">
                  <p>Cada turno posee parámetros de flexibilidad para evitar marcar tardanzas injustificadas por un par de minutos:</p>

                  <ul>
                    <li><b>Tolerancia Entrada (minutos):</b> Si el turno inicia a las 08:00 y la tolerancia es de 10 min, marcar a las 08:08 se registra a tiempo.</li>
                    <li><b>Tolerancia Salida (minutos):</b> Si la salida es a las 17:00 y la tolerancia es de 10 min, marcar a las 16:52 no genera alerta de salida anticipada.</li>
                    <li><b>Redondeo de Marcaciones:</b> Configuración global en <i>Acuerdos Laborales</i> para ajustar marcaciones a intervalos de 5, 10 o 15 minutos.</li>
                  </ul>
                </div>
              </div>
            </div>

          </div> <!-- /panel-group -->

        </div>

        <!-- ========================================== -->
        <!-- OTRAS SECCIONES (PLACEHOLDERS ELEGANTES)   -->
        <!-- ========================================== -->
        <div v-else class="box box-default text-center p-5">
          <i class="fa fa-clock-o fa-4x text-muted margin-bottom"></i>
          <h3>Sección en preparación</h3>
          <p class="text-muted">
            Estamos construyendo las guías interactivas para esta sección. Por el momento, consulte el módulo <b>1. Gestión de Turnos</b> para ver todas las explicaciones y ejemplos completos.
          </p>
          <button class="btn btn-primary margin-top" @click="activeSection = 'shifts'">
            <i class="fa fa-arrow-left"></i> Volver a Gestión de Turnos
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Tutorial",
  data() {
    return {
      activeSection: "shifts",
      searchQuery: "",
      expandAll: true,
      selectedSimShift: "PRENOC"
    };
  },
  methods: {
    matchesSearch(keywords) {
      if (!this.searchQuery) return true;
      const query = this.searchQuery.toLowerCase().trim();
      return keywords.toLowerCase().includes(query) || query.split(" ").some(q => keywords.toLowerCase().includes(q));
    }
  }
};
</script>

<style scoped>
.tutorial-container {
  font-family: 'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.tutorial-header-box {
  border-top-width: 3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
}

.tutorial-title {
  margin-top: 0;
  font-weight: 700;
  font-size: 24px;
  color: #2c3e50;
}

.tutorial-subtitle {
  font-size: 14px;
  margin-bottom: 0;
}

.search-box {
  margin-top: 10px;
}

.tutorial-sidebar li a {
  font-weight: 600;
  border-radius: 3px;
  padding: 12px 15px;
}

.tutorial-sidebar li.active a {
  background-color: #3c8dbc !important;
  color: #ffffff !important;
}

.simulator-well {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 15px;
}

.timeline-preview {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.time-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.steps-list {
  padding-left: 20px;
}

.steps-list li {
  margin-bottom: 12px;
  line-height: 1.6;
}

.panel-heading {
  cursor: pointer;
}

.panel-heading a {
  color: #fff !important;
  text-decoration: none;
  display: block;
}

.bg-light-blue-gradient {
  background: linear-gradient(135deg, #3c8dbc 0%, #2980b9 100%);
}

.box-body-light {
  background-color: #ffffff;
  border: 1px solid #e3e8ee;
  border-radius: 4px;
}

.margin-top-5 {
  margin-top: 5px;
}

.p-3 {
  padding: 15px;
}

.p-5 {
  padding: 40px;
}

.margin-bottom {
  margin-bottom: 15px;
}

.margin-top {
  margin-top: 15px;
}
</style>
